import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Book as BookIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  History as HistoryIcon,
  Inventory as InventoryIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  QrCode as QrCodeIcon,
  Storage as StorageIcon,
  SwapHoriz as SwapHorizIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export const DRAWER_WIDTH = 240;

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isSelected = (path: string) => location.pathname === path;
  const getItemColors = (path: string) => {
    if (isSelected(path)) {
      return {
        text: theme.palette.primary.contrastText,
        icon: theme.palette.primary.main,
        background: alpha(theme.palette.primary.main, 0.24),
        hover: alpha(theme.palette.primary.main, 0.32),
      };
    }

    return {
      text: alpha(theme.palette.text.primary, 0.88),
      icon: alpha(theme.palette.text.primary, 0.72),
      background: alpha(theme.palette.primary.main, 0.05),
      hover: alpha(theme.palette.primary.main, 0.14),
    };
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  // 사용자 메뉴
  const userMenuItems = [
    { text: '대시보드', icon: <DashboardIcon />, path: '/dashboard' },
    { text: '프로젝트 관리', icon: <FolderIcon />, path: '/projects' },
    { text: '보유금형 현황', icon: <InventoryIcon />, path: '/dashboard2' },
    { text: '금형 대장', icon: <InventoryIcon />, path: '/molds' },
    { text: '금형 이력관리', icon: <BookIcon />, path: '/molds/history' },
    { text: 'QR 스캔', icon: <QrCodeIcon />, path: '/qr/scan' },
    { text: '입출고 처리', icon: <SwapHorizIcon />, path: '/transactions/new' },
    { text: '입출고 내역', icon: <HistoryIcon />, path: '/transactions' },
    { text: 'AI 챗봇', icon: <ChatIcon />, path: '/chat' },
    { text: '알림 설정', icon: <NotificationsIcon />, path: '/notification-settings' },
    { text: '프로필 설정', icon: <PersonIcon />, path: '/profile' },
  ];

  // 고객사/협력사 메뉴
  const customerMenuItems = [
    { text: '대시보드', icon: <DashboardIcon />, path: '/dashboard' },
    { text: '프로젝트 현황', icon: <FolderIcon />, path: '/projects' },
    { text: '보유금형 현황', icon: <InventoryIcon />, path: '/dashboard2' },
    { text: '금형 현황', icon: <InventoryIcon />, path: '/molds' },
    { text: '금형 이력관리', icon: <BookIcon />, path: '/molds/history' },
    { text: '프로필 설정', icon: <PersonIcon />, path: '/profile' },
  ];

  // 관리자 전용 메뉴
  const adminMenuItems = [
    { text: '금형 등록', icon: <AddIcon />, path: '/molds/new' },
    { text: '거래처 관리', icon: <BusinessIcon />, path: '/customers' },
    { text: '보관장소 관리', icon: <WarehouseIcon />, path: '/locations' },
    { text: '사용자 관리', icon: <PeopleIcon />, path: '/users' },
    { text: '생산 실적 리포트', icon: <AssessmentIcon />, path: '/production-report' },
    { text: '리포트/통계', icon: <AnalyticsIcon />, path: '/reports' },
    { text: '스토리지 설정', icon: <StorageIcon />, path: '/admin/storage' },
  ];

  const renderMenuItems = (items: typeof userMenuItems) => {
    return items.map((item) => (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          selected={isSelected(item.path)}
          onClick={() => handleMenuClick(item.path)}
          sx={{
            minHeight: { xs: 48, sm: 46 },
            borderRadius: 2,
            mx: 1.25,
            mb: 0.75,
            px: 1.5,
            '&:hover': {
              backgroundColor: getItemColors(item.path).hover,
            },
            '&.Mui-selected': {
              backgroundColor: getItemColors(item.path).background,
              '&:hover': {
                backgroundColor: getItemColors(item.path).hover,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 42,
              color: getItemColors(item.path).icon,
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: { xs: '0.9rem', sm: '0.92rem' },
              fontWeight: isSelected(item.path) ? 600 : 500,
              color: getItemColors(item.path).text,
              letterSpacing: '0.01em',
            }}
          />
        </ListItemButton>
      </ListItem>
    ));
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: alpha(theme.palette.text.secondary, 0.9),
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.26em',
          }}
        >
          금형자산관리-EGMMS 
        </Typography>
      </Box>
      <List sx={{ pt: 0 }}>
        {renderMenuItems(
          user?.role === 'customer' || user?.role === 'partner'
            ? customerMenuItems
            : userMenuItems
        )}
      </List>
      
      {/* 관리자 권한이 있을 때만 관리자 메뉴 표시 */}
      {user?.role === 'admin' && (
        <>
          <Divider sx={{ mx: 2, my: 1 }} />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.9),
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.26em',
              }}
            >
              관리자 메뉴
            </Typography>
          </Box>
          <List sx={{ pt: 0 }}>
            {renderMenuItems(adminMenuItems)}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor="left"
      open={isMobile ? open : true}
      onClose={isMobile ? onClose : undefined}
      ModalProps={{
        keepMounted: true, // 모바일 성능 개선
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundImage:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(160deg, rgba(20, 28, 46, 0.94), rgba(20, 28, 46, 0.86))'
              : 'linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 255, 0.95))',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.22)}`,
          backdropFilter: theme.palette.mode === 'dark' ? 'blur(16px)' : 'none',
          paddingBottom: theme.spacing(3),
          color: theme.palette.text.primary,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
